import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from '@consta/uikit/Table';
import cn from 'classnames/bind';
import styles from './styles.styl';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { Theme } from '@consta/uikit/Theme';
import { myPreset } from 'src/utils/myPreset';
import DirectoryInfo from 'src/components/DirectoryInfo';
import {
  columnsAdministrationTable,
  NumberLinesItems,
  RowsJournalTable,
} from 'src/utils/config';
import NumberLines, { ItemType } from 'src/components/NumberLines/NumberLines';
import {
  AccessorForAdministartionTablesEnum,
  FilterPageEnum,
  RoleEnum,
} from 'src/utils/enum';
import AdministrationFilter from './AdministrationFilter';
import { IAdministrationCurrentFilters } from './AdministrationFilter/AdministrationFilter';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useShallowEqualSelector } from 'src/customHooks/useShallowEqualSelector';
import { State } from 'src/redux/reducer';
import { fulfilled, pending } from 'src/redux/constants';
import { useDebouncedCallback } from 'use-debounce';
import { userInitSelector } from 'src/redux/features/userInit/selectors';
import TableLoader from 'src/components/TableLoader';
import CustomTextTooltip from 'src/components/CustomTextTooltip';
import {
  editChangeUsersAdministration,
  loadAllUsersAdministration,
} from 'src/redux/features/administration/allUsersAdministration/actions';
import { allUsersAdministrationSelector } from 'src/redux/features/administration/allUsersAdministration/selectors';
import { IAllUsersDataEnt } from 'src/redux/features/users/allUsers/types';
import { Select } from '@consta/uikit/Select';
import { ITypesInitRoles } from 'src/redux/features/userInit/types';
import userApi from 'src/utils/api/userApi';
import useRequest from 'src/customHooks/useRequest';
import { currentUsersSelector } from 'src/redux/features/users/currentUsers/selectors';
import { Modal } from '@consta/uikit/Modal';
import AccessUserModal from 'src/components/AccessUserModal';

const cx = cn.bind(styles);

export interface UploadFilesData {
  id: number;
  path: string;
  filename: string;
  file_type: number;
  approve: boolean;
}

export interface IBaseFilter {
  page: string;
  per_page: string;
  username?: string;
  mod?: string;
}

const Administration: React.FC = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [infoUserModal, setInfoUserModal] = useState<IAllUsersDataEnt>(null);

  const [activeNumber, setActiveNumber] = useState(NumberLinesItems[0]);

  useEffect(() => setRows(RowsJournalTable), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const [searchValue, setSearchValue] = useState(
    params?.username ? params.username : '',
  );

  const [filter, setFilter] = useState<Partial<IAdministrationCurrentFilters>>(
    {},
  );

  const [{ pages, entities, status, count }, userInit, currentUser] =
    useShallowEqualSelector((state: State) => [
      allUsersAdministrationSelector(state),

      userInitSelector(state),
      currentUsersSelector(state),
    ]);

  const clearFilter = () => {
    applyFilters({});
    setSearchValue(null);
  };

  const [currentPage, setCurrentPage] = useState(
    params?.page ? Number(params?.page) : 1,
  );

  // requests
  const { load: fetchEditUser } = useRequest(userApi.loadEditUsers, (data) => {
    if (data) {
      const newData = {
        id: data.id,
        user: data,
      };
      dispatch(editChangeUsersAdministration(newData));
    }
  });

  const filtersAreNotEmpty: boolean = useMemo(
    () =>
      Object.keys(filter).some(
        (key: keyof IAdministrationCurrentFilters) => !!filter[key],
      ) || !!params?.fullname,
    [filter, params],
  );

  const handleAllSearch = useDebouncedCallback((value: string) => {
    searchParams.set(FilterPageEnum.PAGE, '1');
    if (!value) {
      searchParams.delete(FilterPageEnum.USERNAME);
      setSearchParams(searchParams);
    } else if (value?.length > 2) {
      searchParams.set(FilterPageEnum.USERNAME, value);
      setSearchParams(searchParams);
    }
  }, 800);

  const handleSearchName = useCallback(
    (value: string) => {
      setSearchValue(value);
      handleAllSearch(value);
    },
    [handleAllSearch],
  );

  const handleClickFilterIcon = () => {
    setIsAdvancedFilterOpen((prev) => !prev);
  };

  const closeFilter = useCallback(
    () => setIsAdvancedFilterOpen(false),
    [setIsAdvancedFilterOpen],
  );

  useEffect(() => {
    location.search && dispatch(loadAllUsersAdministration(location.search));
  }, [location.search]);

  useEffect(() => {
    const data: { [key: string]: string | number | string[] } = {};

    searchParams.forEach(
      (
        value,
        key: keyof Partial<IAdministrationCurrentFilters> | FilterPageEnum,
      ) => {
        if (
          key !== FilterPageEnum.PAGE &&
          key !== FilterPageEnum.PER_PAGE &&
          key !== FilterPageEnum.USERNAME
        ) {
          data[key as keyof IAdministrationCurrentFilters] = value;
        }
      },
    );

    setFilter((prev) => ({ ...prev, ...data }));
    if (Object.keys(params).length) {
      setCurrentPage(params?.page ? Number(params?.page) : 1);
      setActiveNumber(
        params?.per_page
          ? { id: Number(params?.per_page), label: Number(params?.per_page) }
          : NumberLinesItems[0],
      );
      setSearchValue(params?.username ? params.username : '');
    } else {
      setSearchParams(
        {
          [FilterPageEnum.PER_PAGE]: String(activeNumber.label),
          [FilterPageEnum.PAGE]: String(currentPage),
        },
        {
          replace: true,
        },
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = useCallback((filter) => {
    setFilter(filter);
    const data = Object.fromEntries(
      Object.keys(filter)
        .filter(
          (key: keyof Partial<IAdministrationCurrentFilters>) =>
            filter[key] !== null,
        )
        .map((key: keyof Partial<IAdministrationCurrentFilters>) => [
          key,
          filter[key],
        ]),
    );
    setCurrentPage(1);
    const baseFilter: IBaseFilter = {
      page: '1',
      per_page: String(activeNumber.id),
    };
    if (searchValue) {
      baseFilter.username = searchValue;
    }
    setSearchParams(
      { ...baseFilter, ...data },
      {
        replace: true,
      },
    );
  }, []);

  const changePaginationPerPage = useCallback(
    (value: ItemType) => {
      searchParams.set(FilterPageEnum.PER_PAGE, String(value.label));
      setSearchParams(searchParams);
      setActiveNumber(value);
    },
    [searchParams, setSearchParams],
  );

  const onChangePagination = useCallback(
    (value: number) => {
      searchParams.set(FilterPageEnum.PAGE, String(value + 1));
      setSearchParams(searchParams);
      setCurrentPage(value + 1);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (status === fulfilled) {
      setRows(entities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, status]);

  const handleChangeSelect = useCallback(
    (id: string, value: ITypesInitRoles) => {
      fetchEditUser(id, { role_name: value.key });
    },
    [fetchEditUser],
  );

  const numberModels = useCallback((row: IAllUsersDataEnt) => {
    if (
      row.role_name === RoleEnum.ADMIN ||
      row.role_name === RoleEnum.MODERATOR
    ) {
      return 'Все';
    } else if (row.role_name === RoleEnum.USER) {
      return row.number_available_models;
    } else {
      return '—';
    }
  }, []);

  const clickNumberModels = useCallback((row: IAllUsersDataEnt) => {
    setIsOpenModal(true);
    setInfoUserModal(row);
  }, []);

  const columns = columnsAdministrationTable.map(
    ({ title, accessor, ...rest }): any => {
      if (accessor === AccessorForAdministartionTablesEnum.Fio) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Administration__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: IAllUsersDataEnt) => (
            <CustomTextTooltip
              className={cx('Administration__ellipsis')}
              text={row.fio || '—'}
              size="s"
            />
          ),
          ...rest,
        };
      } else if (accessor === AccessorForAdministartionTablesEnum.Username) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Administration__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: IAllUsersDataEnt) => (
            <CustomTextTooltip
              className={cx('Administration__ellipsis')}
              text={row.username ?? ''}
              offsetHeight={20}
              lineClamp={1}
              size="s"
            />
          ),
          ...rest,
        };
      } else if (accessor === AccessorForAdministartionTablesEnum.RoleName) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Administration__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: IAllUsersDataEnt) => (
            <Select
              className={cx('Administration__select')}
              items={userInit?.roles}
              onChange={({ value }) => {
                handleChangeSelect(row.id, value);
              }}
              value={userInit.roles.find((item) => item.key === row.role_name)}
              getItemLabel={(item) => item.name}
              getItemKey={(item) => item.key}
              disabled={currentUser.id === row.id}
            />
          ),
          ...rest,
        };
      } else if (
        accessor === AccessorForAdministartionTablesEnum.NumberAvailableModels
      ) {
        return {
          title: (
            <Text
              size="s"
              weight="regular"
              className={cx('Administration__tableHeader')}
            >
              {title}
            </Text>
          ),
          accessor,
          renderCell: (row: IAllUsersDataEnt) => (
            <Text
              size="s"
              className={cx('Administration__numberAvailable')}
              view={row.role_name === RoleEnum.USER ? 'link' : 'primary'}
              onClick={() =>
                row.role_name === RoleEnum.USER && clickNumberModels(row)
              }
            >
              {numberModels(row)}
            </Text>
          ),
          ...rest,
        };
      }
      return {
        title: (
          <Text
            size="s"
            weight="regular"
            className={cx('Administration__tableHeader')}
          >
            {title}
          </Text>
        ),
        accessor,
        ...rest,
      };
    },
  );

  return (
    <div className={cx('Administration')}>
      <DirectoryInfo
        viewButton="secondary"
        handleSearch={handleSearchName}
        searchValue={searchValue}
        handleClickFilter={handleClickFilterIcon}
        className={cx('Administration__directoryInfo')}
        hasFilter={Object.keys(filter).length > 0}
        plcSeach={'Поиск по логину'}
      />
      {filtersAreNotEmpty && (
        <div className={cx('Administration__tableInfoFilter')}>
          <Text size="xs">Найдено: {count}</Text>
          <Button
            size="xs"
            view="ghost"
            label="Сбросить все фильтры"
            onClick={clearFilter}
          />
        </div>
      )}
      {status === pending ? (
        <TableLoader />
      ) : (
        <Theme preset={myPreset}>
          <Table
            columns={columns}
            rows={rows}
            zebraStriped="even"
            size="m"
            verticalAlign="center"
            className={cx('Administration__table', {
              Administration__table_filter: filtersAreNotEmpty,
            })}
          />
        </Theme>
      )}
      <AdministrationFilter
        isOpen={isAdvancedFilterOpen}
        close={closeFilter}
        filter={filter}
        setFilter={applyFilters}
        clearFilter={clearFilter}
      />
      <div className={cx('Administration__pag')}>
        {pages && (
          <NumberLines
            valuePage={activeNumber}
            handleChange={changePaginationPerPage}
            currentPage={currentPage - 1}
            totalPages={pages}
            onChangePagination={onChangePagination}
          />
        )}
      </div>
      <Modal
        position="center"
        isOpen={isOpenModal}
        hasOverlay
        className={cx('Administratio__modalView')}
      >
        <AccessUserModal
          infoUser={infoUserModal}
          setIsOpenModal={setIsOpenModal}
        />
      </Modal>
    </div>
  );
};
export default Administration;
